import { z } from "zod";
import { Email, Password, Role } from "./auth.js";

// ---------------------------------------------------------------------------
// 退会（縦串7、026、docs/21）。射場さん 9/17 の決定：
//   「退会」を押した瞬間にログイン不可 → 30日の猶予（メールのリンクで元に戻せる）→ 30日後に会社ごと消す。
//   退会の前に「データを持ち出す」（出した見積書・請求書の PDF と一覧の CSV を zip に）。理由は複数選択＋自由記述（任意）
//   2026-11-01（#285 の続き）：節「アンケート」＝① 製品内容（いくつでも）② 移行先（1つ・①で「他社の製品に移る」のときだけ）③ 退会理由（自由記述）。
//   選択式の回答は匿名の記録に写し、自由記述は写さない（運営担当者が確認し、30日で会社と一緒に消える）
// ---------------------------------------------------------------------------

export const ACCOUNT_MSG = {
  passwordRequired: "確かめるために、いまのパスワードを入れてください",
  passwordWrong: "パスワードが違います",
  confirmRequired: "「消えることを分かったうえで退会する」にチェックを入れてください",
  closed: "退会を受け付けました。30日以内なら、メールのリンクで元に戻せます",
  /** ログイン・会員登録で、退会の手続き中のメールだったとき */
  accountClosed: "このメールアドレスは退会の手続き中です。元に戻すには、退会のときに届いたメールのリンクを押してください",
  alreadyClosed: "この会社はもう退会の手続き中です",
  notDue: "まだ消す日ではありません",
  reopened: "元に戻しました。今までどおり使えます",
  reopenSent: "退会の手続き中なら、元に戻すためのメールを送りました。届かないときは迷惑メールも確かめてください",
  reasonTooLong: "退会理由は1,000文字までです",
  destinationBad: "移行先を選び直してください",
} as const;

/**
 * 退会のアンケートの設問「製品内容」（いくつでも）。匿名の記録（tenant_closures.reasons）に写す。並びは画面の順。
 * コードは変えない（今ある退会の記録・管理アプリの集計とつながる）。quality・support・trial は 2026-11-01 に足した。
 * DB の CHECK（tenants_close_reasons_codes・tenant_closures_reasons_codes）も同じ9つ
 */
export const CloseReason = z.enum(["features", "price", "hard", "quality", "support", "other_service", "not_using", "trial", "other"]);
export type CloseReason = z.infer<typeof CloseReason>;
export const CLOSE_REASON_LABEL: Record<CloseReason, string> = {
  features: "使いたい機能がない",
  price: "料金が高い",
  hard: "使い方が分からない",
  quality: "動作が遅い・不具合が多い",
  support: "サポートの対応に不満",
  other_service: "他社の製品に移る",
  not_using: "利用する機会がなくなった",
  trial: "試しに登録しただけ",
  other: "その他",
};

/**
 * 退会のアンケートの設問「移行先」（1つ。製品内容で other_service を選んだときだけ）。匿名の記録（tenant_closures.destination）に写す。
 * 候補は製品ごとに他社調査から6つ（2026-11-01 射場さんの確認）＋excel_paper・other。DB の CHECK は両製品を合わせた12のコード
 */
export const CloseDestination = z.enum(["andpad", "aipia", "anyone", "board", "freee", "misoca", "mf", "yayoi", "rakuraku", "zaico", "excel_paper", "other"]);
export type CloseDestination = z.infer<typeof CloseDestination>;
export const CLOSE_DESTINATION_LABEL: Record<CloseDestination, string> = {
  andpad: "ANDPAD",
  aipia: "アイピア",
  anyone: "AnyONE",
  board: "Board",
  freee: "freee",
  misoca: "Misoca",
  mf: "マネーフォワード",
  yayoi: "弥生",
  rakuraku: "楽楽販売",
  zaico: "zaico",
  excel_paper: "Excel・紙に戻る",
  other: "その他",
};
/** 製品ごとの移行先の候補（画面の並び）。現場力＝要件定義の競合と建設向けの比べ・営業力＝要件定義 §5 と docs/07（eigyouryoku） */
export const CLOSE_DESTINATIONS = {
  genba: ["andpad", "aipia", "anyone", "board", "freee", "misoca", "excel_paper", "other"],
  eigyo: ["board", "freee", "mf", "yayoi", "rakuraku", "zaico", "excel_paper", "other"],
} as const satisfies Record<"genba" | "eigyo", readonly CloseDestination[]>;

/**
 * 退会理由の自由記述を保存する前に伏せる（#285 の続き、2026-11-01、docs/21）：URL・メールアドレス・電話番号の形を *** に置き換える。
 * API が受け取った瞬間に通し、DB の fn_free_text_scrub（fn_tenant_close が通す）も同じ決まり＝FREE_TEXT_SCRUB_CASES で両方を確かめる。
 * 先に NFKC で揃える（全角の数字・＠・－も見つける。全角英数は半角になる）。住所・氏名は形で見分けられないので伏せない（画面の注記で頼む）。
 * 順番は URL → メールアドレス → 電話番号（URL の中の数字・@ を先に消す）
 */
export const FREE_TEXT_MASK = "***";
const SCRUB_URL = /(https?:\/\/|www\.)[-A-Za-z0-9._~:/?#@!$&'()*+,;=%]+/gi;
const SCRUB_EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g;
const SCRUB_PHONE = /(?<![0-9])(\+81[- ]?|0)[0-9]([- ().ー‐−–—]{0,2}[0-9]){8,9}(?![0-9])/g;
export function scrubFreeText(s: string): string {
  return s.normalize("NFKC").replace(SCRUB_URL, FREE_TEXT_MASK).replace(SCRUB_EMAIL, FREE_TEXT_MASK).replace(SCRUB_PHONE, FREE_TEXT_MASK);
}
/** 伏せ方の確かめの例（契約の Vitest と、API のテストで DB の fn_free_text_scrub にも同じ入力を通して同じ答えかを見る） */
export const FREE_TEXT_SCRUB_CASES: ReadonlyArray<readonly [string, string]> = [
  ["字が小さくて読みにくい", "字が小さくて読みにくい"],
  ["連絡は taro.yamada@example.co.jp まで", "連絡は *** まで"],
  ["メール ＴＡＲＯ＠ＥＸＡＭＰＬＥ．ＪＰ", "メール ***"],
  ["電話 06-6429-4800 か 090-1234-5678 へ", "電話 *** か *** へ"],
  ["電話 ０６（６４２９）４８００", "電話 ***"],
  ["+81 90 1234 5678 でも", "*** でも"],
  ["https://example.com/a?b=1 を見て", "*** を見て"],
  ["www.example.jp/path まで", "*** まで"],
  ["見積 12 件・請求書 3 枚・2026年11月1日", "見積 12 件・請求書 3 枚・2026年11月1日"],
  ["郵便番号 560-0081", "郵便番号 560-0081"],
  ["連絡先 https://a.example.jp/x と a@b.jp と 0120-123-456", "連絡先 *** と *** と ***"],
];

export const CloseRequest = z.object({
  /** パスワードのある人は必須（API が見る）。パスワードの無い人（Google・Apple・パスキーだけ）は空で、本人確認の窓で確かめる（#189） */
  password: z.string().max(200).default(""),
  /** 「消えることを分かったうえで退会する」のチェック */
  confirm: z.literal(true, { message: ACCOUNT_MSG.confirmRequired }),
  reasons: z.array(CloseReason).max(9).default([]),
  /** 移行先（製品内容に other_service があるときだけ意味を持つ。無ければ API が空にする。製品の候補にあるかは API が見る） */
  destination: CloseDestination.nullable().default(null),
  reasonText: z.string().trim().max(1000, ACCOUNT_MSG.reasonTooLong).default(""),
});
export type CloseRequest = z.infer<typeof CloseRequest>;

export const CloseResult = z.object({
  /** この日時を過ぎると消える（ISO） */
  purgeAfter: z.string(),
  /** 元に戻すリンクを送った先 */
  email: z.string(),
});
export type CloseResult = z.infer<typeof CloseResult>;

export const ReopenRequest = z.object({ token: z.string().regex(/^[0-9a-f]{64}$/, "リンクが正しくありません") });
export type ReopenRequest = z.infer<typeof ReopenRequest>;

export const ResendReopenRequest = z.object({ email: Email });
export type ResendReopenRequest = z.infer<typeof ResendReopenRequest>;

/** 猶予の日数（画面の文と API で同じ値を使う。DB の既定 interval '30 days' と合わせる） */
export const CLOSE_GRACE_DAYS = 30;

// ---------------------------------------------------------------------------
// アカウント → 個人設定（縦串11、034、docs/26。射場さん 2026-09-20）
//   ヘッダーの「アカウント」メニュー → 個人設定：氏名（姓・名、どちらも必須）・メールアドレス（見るだけ）・
//   アカウント権限（第1版は 1メール＝1会社なので全員「管理者」。見るだけ）・パスワードの変更。
//   パスワードを変えると、ほかの端末のログインが切れ、登録のメールアドレスに知らせが届く
//   （クレジットカード・セキュリティガイドライン 附属文書20 別紙a No.12「属性情報変更時の通知」）
// ---------------------------------------------------------------------------

export const PROFILE_MSG = {
  familyNameRequired: "姓を入れてください",
  givenNameRequired: "名を入れてください",
  nameTooLong: "50文字までです",
  nameBadChar: "改行などは入れられません",
  saved: "保存しました",
  currentPasswordRequired: "現在のパスワードを入力してください",
  currentPasswordWrong: "現在のパスワードが違います",
  samePassword: "現在のパスワードと同じです。別のパスワードにしてください",
  passwordChanged: "パスワードを変えました。ほかの端末のログインは解除されました",
  locked: "パスワードの間違いが続いたため、30分のあいだ変更できません",
} as const;

const NO_CONTROL = /^[^\u0000-\u001f\u007f]*$/;
const namePart = (required: string) =>
  z
    .string()
    .transform((v) => v.replace(/^[\s\u3000]+|[\s\u3000]+$/g, ""))
    .pipe(z.string().min(1, required).max(50, PROFILE_MSG.nameTooLong).regex(NO_CONTROL, PROFILE_MSG.nameBadChar));

export const ProfileUpdate = z.object({
  familyName: namePart(PROFILE_MSG.familyNameRequired),
  givenName: namePart(PROFILE_MSG.givenNameRequired),
});
export type ProfileUpdate = z.infer<typeof ProfileUpdate>;

export const Profile = z.object({
  email: z.string(),
  familyName: z.string(),
  givenName: z.string(),
  role: Role,
  /** パスワードを最後に変えた日時（ISO）。Google・Apple だけで作った人（#112）はパスワードが無いので null */
  passwordUpdatedAt: z.string().nullable(),
  /** 前回のログイン（ISO）。まだ無ければ null */
  lastLoginAt: z.string().nullable(),
});
export type Profile = z.infer<typeof Profile>;

export const PasswordChangeRequest = z.object({
  currentPassword: z.string().min(1, PROFILE_MSG.currentPasswordRequired).max(200, PROFILE_MSG.currentPasswordWrong),
  newPassword: Password,
});
export type PasswordChangeRequest = z.infer<typeof PasswordChangeRequest>;

/** パスワードが無い人（Google・Apple だけ、#112）が初めて決める（POST /account/password。いまのパスワードは聞かない） */
export const PasswordSetRequest = z.object({ newPassword: Password });
export type PasswordSetRequest = z.infer<typeof PasswordSetRequest>;

/** アカウント権限の呼び名と、できること（個人設定に出す。第1版は全員 admin） */
export const ROLE_INFO: Record<Role, { label: string; desc: string }> = {
  admin: { label: "管理者", desc: "見積・請求のすべての仕事に加えて、会社の設定・ユーザー管理・料金プラン・退会ができます" },
  manager: { label: "マネージャー", desc: "（使っていません）" },
  leader: { label: "リーダー", desc: "（使っていません）" },
  member: { label: "担当者", desc: "見積・請求の仕事はぜんぶできます。会社の設定・ユーザー管理・料金プラン・退会は管理者だけです" },
};
